<template>
  <div class="row main-content">
    <div class="col">
      <h1 class="page-title font-custom text-uppercase mt-3">
        {{ $t('title.profile.list') }}
      </h1>
      <div class="alert alert-warning">
        <strong>{{ $t('label.profile.warning') }}</strong>
        <p>{{ $t('label.profile.information') }}</p>
      </div>

      <div
        v-if="isProcessing"
        class="text-center"
      >
        <img
          src="@/assets/img/loader.gif"
          class="mx-auto d-block center-block"
        >
      </div>

      <!-- Table -->
      <div
        v-else
        class="table-responsive"
      >
        <table class="table table-hover table-sm table-striped border">
          <thead class="font-custom">
            <tr>
              <th>{{ $t('label.profile.id') }}</th>
              <th>{{ $t('label.profile.description') }}</th>
              <th>{{ $t('label.profile.internal') }}</th>
              <th>{{ $t('label.profile.lot') }}</th>
              <th>{{ $t('label.profile.permissionsNb') }}</th>
              <th>{{ $t('label.profile.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="profile in profiles"
              :key="profile.id"
              class="mb-3"
            >
              <td>{{ profile.id }}</td>
              <td>{{ profile.description }}</td>
              <td>{{ profile.internal }}</td>
              <td>{{ profile.lot }}</td>
              <td>{{ profile.permissionsList.length }}</td>
              <td>
                <BaseButton
                  type="button"
                  class="btn-primary btn-sm d-inline border me-2"
                  @click-base-button-event-emitter="editProfile(profile)"
                >
                  {{ $t('button.edit') }}
                </BaseButton>
                <BaseButton
                  type="button"
                  class="btn-white btn-sm d-inline me-2"
                  data-bs-toggle="modal"
                  data-bs-target="#permissionModal"
                  @click-base-button-event-emitter="showPermissions(profile.permissionsList)"
                >
                  {{ $t('label.profile.permissions') }}
                </BaseButton>
                <BaseButton
                  type="button"
                  class="btn-danger btn-sm d-inline border me-2"
                  href="deletionModal"
                  @click-base-button-event-emitter="showConfirmation (profile, deleteProfile)"
                >
                  {{ $t('button.delete') }}
                </BaseButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- The Modal -->
    <div
      id="permissionModal"
      class="modal fade"
      aria-labelledby="modalTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header">
            <h4
              id="modalTitle"
              class="modal-title"
            >
              {{ $t('label.profile.rolePermissions') }}
            </h4>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <!-- Modal body -->
          <div class="modal-body">
            <table class="table table-hover table-sm table-striped border">
              <thead class="font-custom">
                <tr>
                  <th>{{ $t('label.profile.permissionName') }}</th>
                  <th>{{ $t('label.profile.permissionDescription') }}</th>
                  <!-- th>Actions</th -->
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="permission in permissions"
                  :key="permission.id"
                  class="mb-3"
                >
                  <td>{{ permission }}</td>
                  <td>{{ getPermissionInfo(permission) }}</td>
                  <!-- td></td -->
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Modal footer -->
          <div class="modal-footer">
            <button
              type="button"
              class="base-button font-custom rounded-pill btn-danger btn-sm d-inline border"
              data-bs-dismiss="modal"
            >
              {{ $t('label.profile.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      id="successModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="successModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="successModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.SUCCESS_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-success">
              <p id="successModalMsg" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div
      id="errorModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="errorModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="errorModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.ERROR_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <p id="errorModalMsg" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div
      id="confirmationModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="confirmationModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="confirmationModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              {{ $t('label.modal.CONFIRMATION_MODAL_MSG') }}
            </div>
          </div>
          <div class="modal-footer">
            <BaseButton
              type="button"
              data-bs-dismiss="modal"
              class="btn btn-primary"
              @click-base-button-event-emitter="executeConfirmedAction()"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_YES_BUTTON') }}
            </BaseButton>
            <button
              type="button"
              class="btn rounded-pill btn-secondary font-custom"
              data-bs-dismiss="modal"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_NO_BUTTON') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="./ProfileList.js"></script>

<style src="./ProfileList.scss" scoped lang="scss"></style>
